import React from 'react';
import Modal from './Modal';
import Constants from "../constants";
import { inclusiveIntList } from "../utils";
import { GameOptions, DifficultyLevel } from "../types";
import { PersistantBoolean, PersistantNumber } from "../persistantValue";

interface OptionsPopupState {
    isOpen: boolean;
    size: string;
    height: string;
    width: string;
    squareBoard: boolean;
    symmetricBoard: boolean;
    quickStart: boolean;
    sameSolutionAsPreviousGame: boolean;
    difficulty: DifficultyLevel;
    constraintMinimizationAttempts: number;
}

class OptionsPopup extends React.Component<{}, OptionsPopupState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            isOpen: false,
            size: Constants.sizePV.getString(),
            height: Constants.heightPV.getString(),
            width: Constants.widthPV.getString(),
            squareBoard: Constants.squareBoardPV.getValue(),
            symmetricBoard: Constants.defaultGameOptions.symmetricBoard,
            quickStart: Constants.quickStartPV.getValue(),
            sameSolutionAsPreviousGame: Constants.defaultGameOptions.sameSolutionAsPreviousGame,
            difficulty: Constants.defaultGameOptions.difficulty,
            constraintMinimizationAttempts: Constants.defaultGameOptions.constraintMinimizationAttempts,
        };
    }

    open = (): void => this.setState({ isOpen: true });
    close = (): void => this.setState({ isOpen: false });

    private setSelectOptionFn(pv: PersistantBoolean | PersistantNumber) {
        return (event: React.ChangeEvent<HTMLSelectElement>): void => {
            const optionString = event.target.value;
            pv.setValue(optionString);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.setState({ [pv.name]: optionString } as any);
        };
    }

    private setSquare = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const checked = event.target.checked;
        Constants.squareBoardPV.setValue(checked);
        this.setState({ squareBoard: checked });
    };

    private setSymmetric = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ symmetricBoard: event.target.checked });
    };

    private setQuickStart = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const checked = event.target.checked;
        Constants.quickStartPV.setValue(checked);
        this.setState({ quickStart: checked });
    };

    private setSameSolutionAsPreviousGame = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ sameSolutionAsPreviousGame: event.target.checked });
    };

    getGameOptions(): GameOptions | undefined {
        const size =
            Constants.squareBoardPV.getValue()
                ? {
                    height: Constants.sizePV.getValue(),
                    width: Constants.sizePV.getValue(),
                }
                : {
                    height: Constants.heightPV.getValue(),
                    width: Constants.widthPV.getValue(),
                };

        const { sameSolutionAsPreviousGame, difficulty, symmetricBoard, quickStart, constraintMinimizationAttempts } = this.state;
        const displayOptions = {};

        return {
            ...Constants.defaultGameOptions,
            size,
            sameSolutionAsPreviousGame,
            difficulty,
            symmetricBoard,
            quickStart,
            displayOptions,
            constraintMinimizationAttempts,
        };
    }

    render(): React.ReactNode {
        return (
            <>
                <button className="button game-button game-button__options" onClick={this.open}>
                    <span className="game-button__icon">⚙️</span>
                    Options
                </button>

                <Modal isOpen={this.state.isOpen} onClose={this.close} title="Light Up Options">
                    <label>
                        <input type="checkbox" checked={this.state.squareBoard} onChange={this.setSquare} />
                        Square Board?
                    </label>

                    <div className="stack">
                        {this.state.squareBoard ? (
                            <label>
                                Size:&nbsp;
                                <select
                                    name="size"
                                    value={this.state.size}
                                    onChange={this.setSelectOptionFn(Constants.sizePV)}
                                >
                                    {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map((s) => (
                                        <option value={s.toString()} key={s.toString()}>
                                            {s.toString()}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : (
                            <>
                                <label>
                                    Height:&nbsp;
                                    <select
                                        name="height"
                                        value={this.state.height}
                                        onChange={this.setSelectOptionFn(Constants.heightPV)}
                                    >
                                        {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map((s) => (
                                            <option value={s.toString()} key={s.toString()}>
                                                {s.toString()}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Width:&nbsp;
                                    <select
                                        name="width"
                                        value={this.state.width}
                                        onChange={this.setSelectOptionFn(Constants.widthPV)}
                                    >
                                        {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map((s) => (
                                            <option value={s.toString()} key={s.toString()}>
                                                {s.toString()}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </>
                        )}
                    </div>

                    <h2>Puzzle Generation Rules</h2>

                    <label>
                        <input
                            type="checkbox"
                            checked={this.state.symmetricBoard}
                            onChange={this.setSymmetric}
                        />
                        Should the generated board be rotationally symmetric?
                    </label>

                    <label>
                        <input
                            type="checkbox"
                            checked={this.state.quickStart}
                            onChange={this.setQuickStart}
                        />
                        Should we save you a little time by x'ing off all the neighbours of zero
                        constraints?
                    </label>

                    <h2>Display Options</h2>
                </Modal>
            </>
        );
    }
}

export default OptionsPopup;
